export const TIPOS = ['VIS', 'CAM', 'REG', 'REL', 'INT', 'FUN', 'MOD', 'BUG'] as const;
export const PRIORIDADES = ['P1', 'P2', 'P3', 'P4'] as const;
export const TAMANHOS = ['PP', 'P', 'M', 'G', 'GG'] as const;
export const ETAPAS = ['fila', 'andamento', 'concluida'] as const;

export type Etapa = (typeof ETAPAS)[number];

export interface Criterio {
  id: string;
  texto: string;
  feito: boolean;
}

export interface Coluna {
  id: string;
  quadro_id: string;
  nome: string;
  codigo: string | null;
  ordem: number;
  etapa: Etapa;
  limite_wip: number | null;
  exige_aprovacao: boolean;
  espera_pessoa: boolean;
  total: number;
  exibidos?: number;
  filtrados?: number;
}

export interface Quadro {
  id: string;
  nome: string;
  codigo: string | null;
  descricao: string | null;
  ordem: number;
  /** Pacote 2 (Projetos e tarefas): projeto do Cadastros do quadro, visibilidade e arquivamento. */
  projeto_id?: string | null;
  visibilidade?: Visibilidade;
  arquivado_em?: string | null;
  colunas: Coluna[];
  abertos: number;
}

export const VISIBILIDADES = ['todos', 'equipe'] as const;
export type Visibilidade = (typeof VISIBILIDADES)[number];
export const STATUS_PROJETO = ['planejado', 'em_andamento', 'pausado', 'concluido', 'cancelado'] as const;
export type StatusProjeto = (typeof STATUS_PROJETO)[number];

/** Projeto (Pacote 2): o quadro ligado a um projeto do Cadastros; os dados do Cadastros vêm nulos sem `cadastros.ver`. */
export interface Projeto {
  id: string;
  nome: string;
  descricao: string | null;
  projeto_id: string;
  visibilidade: Visibilidade;
  arquivado_em: string | null;
  created_at: string;
  tarefas_abertas: number;
  tarefas_concluidas: number;
  tarefas_atrasadas: number;
  marcos_total: number;
  marcos_concluidos: number;
  proximo_marco: { id: string; nome: string; data: string; atrasado: boolean } | null;
  membros: { id: string; nome: string }[];
  cadastro_visivel: boolean;
  cliente_id: string | null;
  cliente_nome: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  inicio: string | null;
  prazo: string | null;
  orcamento_centavos: number | null;
  moeda: string | null;
  status: StatusProjeto | null;
  atrasado: boolean;
}

export interface Marco {
  id: string;
  quadro_id: string;
  quadro_nome: string;
  quadro_arquivado: boolean;
  nome: string;
  descricao: string | null;
  data: string;
  concluido_em: string | null;
  concluido_por_nome: string | null;
  atrasado: boolean;
  created_at: string;
}

export interface Card {
  id: string;
  numero: number;
  quadro_id: string;
  quadro_nome: string;
  quadro_codigo: string | null;
  /** Pacote 2: o projeto do quadro está arquivado (a tarefa fica só para consulta). */
  quadro_arquivado?: boolean;
  coluna_id: string;
  coluna_nome: string;
  coluna_codigo: string | null;
  coluna_etapa: Etapa;
  titulo: string;
  descricao?: string | null;
  tipo: string | null;
  prioridade: string;
  tamanho: string | null;
  modulo_alvo: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  prazo: string | null;
  atrasado: boolean;
  etiquetas: string[];
  vinculo_entidade: string | null;
  vinculo_id: string | null;
  /** Fase 2 (W23, K11): só na ficha, e só para quem vê o registro vinculado (`vinculo_visivel`). */
  vinculo_titulo?: string | null;
  vinculo_link?: string | null;
  vinculo_visivel?: boolean;
  criterios?: Criterio[];
  criterios_total: number;
  criterios_feitos: number;
  origem: 'tela' | 'agente' | 'chave' | 'sistema';
  solicitante: string | null;
  criador_nome: string | null;
  coluna_desde: string;
  dias_na_coluna: number;
  concluido_em: string | null;
  created_at: string;
  /** V1: verticais do card (vazio = Todas). */
  empresas?: string[];
}

export interface Movimento {
  id: string;
  de_coluna_id: string | null;
  de_nome: string | null;
  de_codigo: string | null;
  para_coluna_id: string;
  para_nome: string;
  para_codigo: string | null;
  ator_tipo: 'usuario' | 'chave_api' | 'sistema';
  autor_nome: string | null;
  created_at: string;
}

export interface Kanban {
  quadro: { id: string; nome: string; codigo: string | null };
  colunas: Coluna[];
  cards: Card[];
  truncado: boolean;
}

export interface Estatistica {
  n: number;
  media: number | null;
  mediana: number | null;
  p85: number | null;
}

export interface Painel {
  periodo: { de: string; ate: string; fuso: string };
  lead_time: Estatistica;
  cycle_time: Estatistica;
  espera_pessoa: { n: number; media_dias: number | null; percentual_do_lead: number | null };
  por_coluna: { coluna_id: string; nome: string; codigo: string | null; etapa: Etapa; espera_pessoa: boolean; passagens: number; media_dias: number | null; atuais: number; gargalo: boolean }[];
  vazao: { semana: string; concluidos: number }[];
  wip: { total: number; colunas: { coluna_id: string; nome: string; codigo: string | null; total: number; limite_wip: number | null }[] };
  retrabalho: { voltas: number; cards_com_volta: number; cards_movidos: number; taxa: number | null };
  idade: { abertos: number; faixas: { ate_2: number; de_3_a_7: number; de_8_a_14: number; mais_14: number }; mais_parados: { id: string; numero: number; titulo: string; coluna: string; coluna_codigo: string | null; dias_parado: number; dias_total: number }[] };
  origem: { totais: { origem: string; n: number }[]; por_quem: { origem: string; quem: string | null; n: number }[] };
  previsibilidade: { tamanho: string | null; n: number; mediana: number | null; p85: number | null }[];
}
